// header.js - Code complet
import React from "react"
import useNavigations from "hooks/useNavigations"
import styled, { css } from "styled-components"
import Search from "./search"
import DesktopMenu from "./DesktopMenu"
import { CmsImage, Dropdown, Container } from "@bw/bits"
import { breakpoints } from "../../../theme"
import useGlobalTheme from "../../../hooks/useGlobalTheme"
import { Link as I18nLink } from "gatsby-plugin-react-i18next"

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  position: var(--header-position, fixed);
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999 !important;
  padding: 12px 0;
  min-height: 55px;
  color: #000;
  background-color: #fff;
  transition: background-color 0.25s, color 0.25s;
  font-size: 16px;
  line-height: 1.2;

  .logo-at-rest {
    opacity: 1;
  }
  .logo-when-scrolled {
    opacity: 0;
  }

  &.active {
    box-shadow: 10px 10px 35px rgba(0, 0, 0, 0.05);
    background: var(--header-background-color-scrolled, #fff);
    color: #95c01f;

    .logo-at-rest {
      opacity: 0;
    }
    .logo-when-scrolled {
      opacity: 1;
    }
  }
`

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const BurgerCheckbox = styled.input`
  display: none;

  &:checked ~ label .burger-line:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  &:checked ~ label .burger-line:nth-child(2) {
    opacity: 0;
  }

  &:checked ~ label .burger-line:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -6px);
  }
`

const BurgerLabel = styled.label`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 100001;
  position: relative;

  .burger-line {
    width: 26px;
    height: 2px;
    background: currentColor;
    transition: all 0.3s ease;
    border-radius: 2px;

    &:not(:last-child) {
      margin-bottom: 5px;
    }
  }

  @media (max-width: ${breakpoints.medium}px) {
    display: flex;
  }
`

const Overlay = styled.label`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99999;
  cursor: pointer;

  @media (min-width: ${breakpoints.medium}px) {
    display: none !important;
  }
`

const MobileMenuWrapper = styled.div`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 90%;
  background: #ffffff;
  z-index: 100000;
  overflow-y: auto;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);

  @media (min-width: ${breakpoints.medium}px) {
    display: none !important;
  }
`

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
`

const CloseButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CloseButton = styled.label`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  color: #1a1a1a;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-weight: 400;

  &:hover {
    opacity: 0.6;
  }
`

const CloseText = styled.span`
  font-size: 16px;
  color: #1a1a1a;
  font-weight: 400;
`

const MenuTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  color: #1a1a1a;
`

const MenuContent = styled.div`
  padding: 32px 0;
`

const MobileLink = styled(I18nLink)`
  font-weight: 700;
  text-decoration: none;
  color: #1a1a1a;
  display: block;
  padding: 40px 10px !important;
  transition: all 0.2s ease;
  font-size: 17px;
  text-align: center;
  letter-spacing: 4px;
  border-bottom: 1px solid #e8e8e8;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-transform: uppercase;

  &:hover {
    background: #f5f5f5;
    color: #95c01f;
  }

  &:active {
    background: #eeeeee;
  }

  &:last-child {
    border-bottom: none;
  }
`

const Navigation = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 12px;

  a {
    margin: 0;
    padding: 0 10px;
    font-size: 18px;
  }

  @media (max-width: 960px) {
    .desktop-menu {
      display: none;
    }
  }

  ${BurgerCheckbox}:checked ~ ${Overlay} {
    display: block;
  }

  ${BurgerCheckbox}:checked ~ ${MobileMenuWrapper} {
    display: block;
    animation: slideInRight 0.3s ease;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`

const LogoWrapper = styled(I18nLink)`
  position: relative;
  display: flex;
  align-items: center;
  color: currentColor;
  ${props => css`
    width: ${props.$width / (props.$highDensity ? 2 : 1)}px;
    height: ${props.$height / (props.$highDensity ? 2 : 1)}px;
  `}

  > div {
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.25s;
  }
`

const StyledLink = styled(I18nLink).attrs(() => ({
  activeClassName: "active",
}))`
  text-decoration: none;
  color: currentColor;
  font-weight: bold;
  transition: opacity 0.25s ease-in-out;

  &:hover {
    opacity: 0.5;
  }
`

const activeStyle = {
  fontWeight: 700,
}

const Header = ({ pageContext, forceNegative }) => {
  const { burger, shortcuts } = useNavigations() || {}
  const theme = useGlobalTheme()

  // const logoNegative = theme.logo_negative ? theme.logo_negative : theme.logo
  const logoNegative = theme?.logo_negative || theme?.logo || {}
  const headerClassname = ["navbar"]

  return (
    <HeaderWrapper className={headerClassname.join(" ")}>
      <Container>
        <Grid>
          <LogoWrapper
            to="/"
            $highDensity={theme?.highDensity}
            $height={theme?.logo?.localImage?.childImageSharp?.original?.height}
            $width={theme?.logo?.localImage?.childImageSharp?.original?.width}
            className="logo"
          >
            <div className="logo-at-rest">
              <CmsImage
                src={theme?.logo?.localImage?.childImageSharp?.original?.src}
                alt={`Logo ${process.env.GATSBY_SITE_TITLE}`}
              />
            </div>
            <div className="logo-when-scrolled">
              <CmsImage
                src={logoNegative?.localImage?.childImageSharp?.original?.src}
                alt={`Logo ${process.env.GATSBY_SITE_TITLE}`}
              />
            </div>
          </LogoWrapper>

          <Navigation>
            <DesktopMenu menu={shortcuts} />
            <LanguageSelector
              current={pageContext.locale}
              translations={pageContext.translations}
            />
            {theme.feature_search && <Search />}
            
            <BurgerCheckbox type="checkbox" id="burger-toggle" />
            <BurgerLabel htmlFor="burger-toggle">
              <span className="burger-line"></span>
              <span className="burger-line"></span>
              <span className="burger-line"></span>
            </BurgerLabel>
            
            <Overlay htmlFor="burger-toggle" />
            
            <MobileMenuWrapper>
              <MenuHeader>
                <CloseButtonWrapper>
                  <CloseButton htmlFor="burger-toggle">×</CloseButton>
                  <CloseText>fermer</CloseText>
                </CloseButtonWrapper>
              </MenuHeader>
              
              <MenuTitle style={{padding: '32px 32px 0 32px'}}>Menu</MenuTitle>
              
              <MenuContent>
                {shortcuts.map((item, i) => (
                  <MobileLink key={i} to={item.to} activeStyle={activeStyle}>
                    {item.label}
                  </MobileLink>
                ))}
              </MenuContent>
            </MobileMenuWrapper>
          </Navigation>
        </Grid>
      </Container>
    </HeaderWrapper>
  )
}

const LanguageSelector = ({ translations, current }) => {
  if (!translations || translations.length <= 1) {
    return null
  }

  if (translations.length === 2) {
    const translation = translations.find(t => t.locale !== current)
    return (
      <StyledLink
        to={translation.slug}
        onClick={() => {
          document.documentElement.setAttribute("lang", translation.locale)
        }}
      >
        {translation.locale.toUpperCase()}
      </StyledLink>
    )
  }

  return (
    <Dropdown
      title={current.toUpperCase()}
      list={translations
        .filter(translation => translation.locale !== current)
        .map(translation => ({
          title: current.toUpperCase(),
          to: translation.slug,
          onClick: () => {
            document.documentElement.setAttribute("lang", translation.locale)
          },
        }))}
    />
  )
}

export { Header }